export default class DfApi {
  async moveDesignElementsToFolder(projectId, designElements, folderId, format = 'json') {
    const folderRef = folderId || -1;
    const url = `/projects/${projectId}/folders/${folderRef}/items`;
    const items = this.#buildDesignElementItems(
      designElements,
      'designElementType',
      'designElementId'
    );
    const body = this.#parsedBody({ items });
    const accept = this.#acceptedContentHeader(format);

    const options = {
      ...this.#postOptions,
      headers: {
        ...this.#postOptions.headers,
        ...(accept.headers || {}),
      },
      ...body,
    };
    const response = await fetch(url, options);

    return response;
  }

  async sortDesignElements(projectId, designElements) {
    const url = `/projects/${projectId}/sort_design_elements`;
    const body = this.#parsedBody({ design_elements: designElements });
    const options = { ...this.#patchOptions, ...body };
    const response = await fetch(url, options);

    return response;
  }

  async saveContract(projectId, contractId, contract) {
    const url = `/projects/${projectId}/contracts/${contractId}`;
    const body = this.#parsedBody({ contract });
    const options = { ...this.#patchOptions, ...body };
    const response = await fetch(url, options);

    return response;
  }

  async saveContractTemplate(templateId, preset) {
    const url = `/my_templates/contracts/${templateId}/`;
    const body = this.#parsedBody({ preset });
    const options = { ...this.#patchOptions, ...body };
    const response = await fetch(url, options);

    return response;
  }

  async buildClientSignatures(projectId, contractId, clientIds) {
    const urlParams = clientIds.map((id) => `signer_ids[]=${id}`).join('&');
    const url = `/projects/${projectId}/contracts/${contractId}/signatures/new/?${urlParams}`;
    const response = await fetch(url);

    return response;
  }

  async assignDesignerToProject(designerId, projectId) {
    const url = `/projects/${projectId}/project_designers`;
    const body = this.#parsedBody({ designer_id: designerId });
    const options = { ...this.#postOptions, ...body };
    const response = await fetch(url, options);

    return response.json();
  }

  async removeDesignerAssignmentFromProject(assignmentId, projectId) {
    const url = `/projects/${projectId}/project_designers/${assignmentId}`;
    const response = await fetch(url, this.#deleteOptions);

    return response.json();
  }

  async getPreset(presetId, projectId, contractId) {
    let url = `/presets/${presetId}.json?project_id=${projectId}`;
    if (contractId) {
      url += `&contract_id=${contractId}`;
    }
    const response = await fetch(url);
    return response;
  }

  async createPreset(title, kind, projectId, details) {
    const url = '/presets.json';
    const body = this.#parsedBody({ title, kind, project_id: projectId, details });
    const options = { ...this.#postOptions, ...body };
    const response = await fetch(url, options);

    return response;
  }

  async deletePreset(id) {
    const url = `/presets/${id}.json`;
    const options = this.#deleteOptions;
    const response = await fetch(url, options);

    return response;
  }

  async saveDesignerNotes(projectId, designerNotes) {
    const url = `/projects/${projectId}/designer_notes`;
    const body = this.#parsedBody({ project: { designer_notes: designerNotes } });
    const options = { ...this.#patchOptions, ...body };
    const response = await fetch(url, options);

    return response;
  }

  async saveTaskNotes(taskId, notes) {
    const url = `/tasks/${taskId}`;
    const body = this.#parsedBody({ task: { notes } });
    const options = { ...this.#patchOptions, ...body };
    const response = await fetch(url, options);

    return response;
  }

  #buildDesignElementItems(items, typeKey = 'item_type', idKey = 'item_id') {
    return items.reduce((obj, { [typeKey]: itemType, [idKey]: itemId }, idx) => {
      obj[idx] = { item_type: itemType, item_id: itemId };
      return obj;
    }, {});
  }

  #parsedBody(body) {
    return { body: JSON.stringify(body) };
  }

  #acceptedContentHeader(format) {
    return { headers: { Accept: this.#responseFormats[format] } };
  }

  get #responseFormats() {
    return {
      turbostream: 'text/vnd.turbo-stream.html',
      json: 'application/json',
    };
  }

  get #patchOptions() {
    return {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': this.#csrfToken, 'Content-Type': 'application/json' },
    };
  }

  get #postOptions() {
    return {
      method: 'POST',
      headers: { 'X-CSRF-Token': this.#csrfToken, 'Content-Type': 'application/json' },
    };
  }

  get #deleteOptions() {
    return {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': this.#csrfToken, 'Content-Type': 'application/json' },
    };
  }

  get #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
